import { clampDifficulty } from '../core/staircase';
import type { ModuleId, ModuleSummary, TrialResult } from '../core/types';

const DEFAULT_STARTING_DIFFICULTY = 3.5;
const TARGET_PERFORMANCE = 0.75;

function trialPerformance(result: TrialResult): number {
  const partial = result.metadata.selectionAccuracy;
  return typeof partial === 'number' && Number.isFinite(partial)
    ? clampDifficulty(partial, 0, 1)
    : result.correct ? 1 : 0;
}

/**
 * A stochastic placement staircase. Large early corrections locate the useful
 * range quickly; smaller later corrections settle near 75% performance.
 */
export function adaptDifficultyToPerformance(
  current: number,
  recentResults: readonly TrialResult[],
): number {
  const valid = recentResults.filter((result) => result.valid);
  const latest = valid.at(-1);
  if (!latest) return clampDifficulty(current, 1, 5);
  const gain = valid.length <= 4 ? 1.6 : valid.length <= 10 ? 1 : 0.65;
  const change = gain * (trialPerformance(latest) - TARGET_PERFORMANCE);
  return Number((Math.round(clampDifficulty(current + change, 1, 5) * 20) / 20).toFixed(2));
}

/** Uses the latest adaptive threshold as the next session's starting point. */
export function startingDifficultyFromSummary(summary?: ModuleSummary): number {
  if (!summary || summary.threshold === undefined || !Number.isFinite(summary.threshold)) {
    return DEFAULT_STARTING_DIFFICULTY;
  }
  const difficultyByModule: Record<ModuleId, number> = {
    'read-switch': 1 + (347 - summary.threshold) / 56.75,
    'central-peripheral': 1 + (725 - summary.threshold) / 90.5,
    'player-track': 1 + (summary.threshold - 7.4) / 1.25,
    'moving-target': 1 + (22 - summary.threshold) / 3,
  };
  return Number(clampDifficulty(difficultyByModule[summary.moduleId], 1, 5).toFixed(2));
}
